<template>
  <div>
    <div class="dashboard-page-title">
      <div class="container-fluid">

        <h1>Public</h1>
        <h4 v-if="$userIsTalent">Browse and apply for engagements</h4>

        <campaign-notification
          v-if="showCampaign && !$userIsManager"
          icon="bullhorn"
          title="1000 Virtual meetings in 100 days"
          heading="Help us bring some inspiration to others during this uncertain period..."
        >
          <template v-slot:buttons>
            <router-link :to="{ name: 'kickoffs-with-pickstar.opt-in' }" class="btn btn btn-primary">
              Find out how
            </router-link>
          </template>
        </campaign-notification>

        <div class="dashboard-results-details">
          <div class="flex flex-wrap justify-between spacer-b-2">
            <div class="count"><span>{{ pagination.total }}</span> public requests</div>
            <booking-list-sort
              :set-filter-mutation="setFilter"
              :filters="filters"
              :options="sortOptions"
            />
          </div>
        </div>

        <booking-list-filters
          :set-filter-mutation="setFilter"
          :filters="filters"
          :apply-filters="handleApplyFiltersCallback">
          <ladda-button
            slot="button"
            :click="handleApplyFiltersCallback"
            type="submit"
            :disabled="$userIsInactiveManager"
            class="btn btn-primary pull-right">
            Search
          </ladda-button>
        </booking-list-filters>

        <pickstar-loading v-if="fetchingBillboardBookings" class="m-t-lg"/>

        <template v-else>
          <no-results v-if="$userIsInactiveManager" class="text-center">
            <div>
              You will be able to view the billboard once {{ clientName }} has approved your account. In the meantime,
              build out your <router-link :to="{ name: 'profile.show' }">profile</router-link> or start
              <router-link :to="{ name: 'talent.index' }">adding your {{ $starLabelPlural }} for review!</router-link>
            </div>
          </no-results>
          <no-results v-else-if="$userIsPending" class="text-center">
            <div>
              You will be able to view the billboard once Pickstar has approved your account. In the meantime, build out your <router-link :to="{ name: 'profile.show' }">profile</router-link>
            </div>
          </no-results>
          <no-results v-else-if="haveNoBillboardBookings" class="text-center">
            <div>There are no bookings on the billboard.</div>

            <button v-if="haveAppliedFilters" @click="handleResetFiltersCallback" class="btn btn-default btn-sm m-t-sm">Reset Filters</button>
          </no-results>

          <ul v-else class="list-items">
            <billboard-list-item
              v-for="booking in bookings"
              :key="booking.id"
              :booking="booking"/>
          </ul>

          <lazy-loading
            v-if="!$userIsInactiveManager"
            class="text-center"
            :loading="loading"
            :reached-bottom="handleGetNextBillboardBookingsCallback"
            ref="lazyLoading">
            <pickstar-loading slot="loading"/>
            <span slot="finished"></span>
          </lazy-loading>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { appliesFilters, handleServerErrors } from 'mixins'
import LazyLoading from 'codium-lazy-loading'
import BillboardListItem from './BillboardListItem'
import BookingListSort from 'components/booking/BookingListSort'
import BookingListFilters from 'components/booking/BookingListFilters'
import NotificationFeedItem from 'components/notifications/NotificationFeedItem'
import CampaignNotification from 'components/campaign/CampaignNotification'

export default {
  mixins: [appliesFilters, handleServerErrors],

  components: {
    LazyLoading,
    BillboardListItem,
    BookingListSort,
    BookingListFilters,
    NotificationFeedItem,
    CampaignNotification
  },

  data () {
    return {
      fetchingBillboardBookings: false,
      showCampaign: false
    }
  },

  computed: {
    ...mapState('app', ['loading']),
    ...mapState('billboard', ['bookings', 'pagination', 'filters']),
    ...mapGetters('billboard', ['haveNoBillboardBookings', 'initialFiltersState']),

    sortOptions () {
      let sortOptions = [
        {
          label: 'Soonest Event Date',
          sort: 'date',
          order: 'asc'
        },
        {
          label: 'Furthest Event Date',
          sort: 'date',
          order: 'desc'
        },
        {
          label: this.monetaryCurrency ? 'Lowest Price' : 'Lowest Points',
          sort: 'price',
          order: 'asc'
        },
        {
          label: this.monetaryCurrency ? 'Highest Price' : 'Highest Points',
          sort: 'price',
          order: 'desc'
        },
        {
          label: 'Newest Opportunities',
          sort: 'vetted_date',
          order: 'desc'
        },
        {
          label: 'Oldest Opportunities',
          sort: 'vetted_date',
          order: 'asc'
        }
      ]
      if (this.$userIsAdmin) {
        sortOptions = sortOptions.concat([
          {
            label: 'Most Recent Creation Date',
            sort: 'created_at',
            order: 'desc'
          },
          {
            label: 'Most Recent Completed Date',
            sort: 'completed_date',
            order: 'desc'
          },
          {
            label: 'Most Recent Booked Date',
            sort: 'scheduled_date',
            order: 'desc'
          },
          {
            label: 'Recently Contacted',
            sort: 'notes.created_at',
            order: 'desc'
          },
          {
            label: 'Least Recently Contacted',
            sort: 'notes.created_at',
            order: 'asc'
          },
          {
            label: 'Most Recently Modified',
            sort: 'updated_at',
            order: 'desc'
          },
          {
            label: 'Least Recently Modified',
            sort: 'updated_at',
            order: 'asc'
          },
          {
            label: 'Most Recently Vetted',
            sort: 'vetted_date',
            order: 'desc'
          },
          {
            label: 'Least Recently Vetted',
            sort: 'vetted_date',
            order: 'asc'
          }
        ])
      }
      return sortOptions
    }
  },

  watch: {
    '$route' () {
      if (!this.$userIsInactiveManager && !this.$userIsPending) {
        this.handleGetBillboardBookingsCallback()
      }
    }
  },

  created () {
    if (!this.$userIsInactiveManager && !this.$userIsPending) {
      this.handleGetBillboardBookingsCallback()
    }
  },

  methods: {
    ...mapActions('billboard', ['getBillboardBookings', 'getNextBillboardBookings', 'setFilter', 'resetFilters']),

    /**
     * Get the billboard bookings.
     *
     * @return Promise
     */
    handleGetBillboardBookingsCallback () {
      this.fetchingBillboardBookings = true

      return this.getBillboardBookings(this.queryParams)
        .catch(this.handleServerErrorsCallback)
        .finally(() => {
          this.fetchingBillboardBookings = false

          this.$nextTick(() => {
            if (!this.$refs.lazyLoading) {
              return
            }

            this.$refs.lazyLoading.$emit('reset')
          })
        })
    },

    /**
     * Get next billboard bookings and handle server errors.
     *
     * @return Promise
     */
    handleGetNextBillboardBookingsCallback () {
      return this.getNextBillboardBookings(this.queryParams)
        .catch(this.handleServerErrorsCallback)
    }
  }
}
</script>
